import {
  forwardRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronRight,
  Info,
  Loader2,
  type LucideIcon,
} from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { intlLocale } from '../lib/i18n';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Primitives for the patient site.
 *
 * Bigger than the desktop app's on purpose: this is read once, on a phone, often
 * one-handed. Every interactive element clears the 24×24 CSS-pixel minimum that
 * WCAG 2.2's Target Size (Minimum) asks for, and every one of them keeps a
 * visible focus ring — `focus:outline-none` without a replacement is the single
 * commonest way a keyboard user is locked out of a form.
 *
 * The look is RxManager's (emerald actions, soft cards), at the patient's scale: 48px controls,
 * 16px text, and a ring that is never thinner than 2px.
 */

/** The focus ring every control shares: 2px of emerald, offset from the control. */
export const FOCUS_RING =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

/** A page's `h1`. It takes focus when the step changes, so its own outline stays off. */
export const HEADING =
  'text-2xl font-semibold tracking-tight text-heading focus:outline-none sm:text-[1.75rem] sm:leading-tight';

/** A native checkbox at a size a thumb can hit, in the page's emerald. */
export const CHECKBOX = cn(
  'mt-0.5 h-5 w-5 shrink-0 cursor-pointer rounded border-input accent-[hsl(var(--primary))]',
  FOCUS_RING,
);

const VARIANTS = {
  primary:
    'bg-primary text-primary-foreground shadow-sm hover:bg-primary-strong disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none',
  outline:
    'border border-input bg-card text-heading hover:border-primary hover:bg-accent disabled:border-border disabled:text-muted-foreground disabled:hover:bg-card',
  ghost: 'text-primary-strong hover:bg-accent disabled:text-muted-foreground',
  danger: 'bg-danger text-danger-foreground shadow-sm hover:bg-danger/90',
  link: 'text-primary-strong underline decoration-primary/40 underline-offset-4 hover:decoration-primary-strong',
} as const;

const SIZES = {
  md: 'min-h-12 px-5 text-base',
  sm: 'min-h-10 px-4 text-sm',
  lg: 'min-h-14 px-6 text-lg',
} as const;

type Variant = keyof typeof VARIANTS;
type Size = keyof typeof SIZES;

/** A button's classes, for a link that has to look like one ("Change or cancel"). */
export function buttonClasses(variant: Variant = 'primary', size: Size = 'md', full = false): string {
  return cn(
    'inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors',
    'disabled:cursor-not-allowed',
    FOCUS_RING,
    variant === 'link' ? 'min-h-11 px-1 text-base' : SIZES[size],
    VARIANTS[variant],
    full && 'w-full',
  );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  full?: boolean;
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  full,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      className={cn(buttonClasses(variant, size, full), className)}
      disabled={disabled || loading}
      {...rest}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
      {children}
    </button>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return (
      <input
        ref={ref}
        className={cn(
          'block min-h-12 w-full rounded-lg border border-input bg-card px-3.5 py-2.5 text-base text-foreground shadow-sm',
          'placeholder:text-muted-foreground hover:border-foreground/50',
          'focus-visible:border-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring',
          'aria-[invalid=true]:border-danger',
          className,
        )}
        {...rest}
      />
    );
  },
);

/**
 * A label, its control, optional help, and an error — wired together.
 *
 * The wiring is the point. `htmlFor` gives the control its name; help and error
 * text are joined into `aria-describedby` so a screen reader announces *why* a
 * field is wrong at the moment it lands on it, rather than the patient hearing
 * "invalid entry" with no idea which rule they broke.
 */
export function Field({
  id,
  label,
  help,
  error,
  required,
  requiredLabel,
  children,
}: {
  id: string;
  label: string;
  help?: string;
  error?: string;
  required?: boolean;
  /** Localised word for "required" — read out, not just shown as an asterisk. */
  requiredLabel?: string;
  children: (props: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': boolean;
    'aria-required': boolean | undefined;
  }) => ReactNode;
}) {
  const helpId = help ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-semibold text-heading">
        {label}
        {required && requiredLabel && (
          <span className="ml-1 font-normal text-muted-foreground">
            ({requiredLabel})
          </span>
        )}
      </label>
      {help && (
        <p id={helpId} className="text-sm text-muted-foreground">
          {help}
        </p>
      )}
      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': Boolean(error),
        'aria-required': required || undefined,
      })}
      {error && (
        <p id={errorId} className="flex items-center gap-1.5 text-sm font-medium text-danger">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}

const CARD_TONES = {
  plain: 'border-border bg-card',
  muted: 'border-transparent bg-muted/70',
  accent: 'border-primary/20 bg-accent',
} as const;

export function Card({
  className,
  tone = 'plain',
  children,
}: {
  className?: string;
  tone?: keyof typeof CARD_TONES;
  children: ReactNode;
}) {
  return (
    <div className={cn('rounded-xl border p-5', CARD_TONES[tone], className)}>{children}</div>
  );
}

const ALERT_TONES = {
  danger: { box: 'border-danger/30 bg-danger/5', icon: AlertCircle, iconClass: 'text-danger' },
  warning: { box: 'border-warning/50 bg-warning/15', icon: AlertTriangle, iconClass: 'text-warning-foreground' },
  info: { box: 'border-primary/25 bg-accent', icon: Info, iconClass: 'text-primary-strong' },
  success: { box: 'border-success/30 bg-success/5', icon: CheckCircle2, iconClass: 'text-success' },
} as const;

/**
 * Something to say, announced.
 *
 * A problem is `role="alert"` so it reaches a screen reader the moment it appears — a patient
 * who cannot see the red box otherwise submits the same broken form again. The quieter tones are
 * `status`: told, without interrupting.
 */
export function Alert({
  children,
  action,
  tone = 'danger',
}: {
  children: ReactNode;
  action?: ReactNode;
  tone?: keyof typeof ALERT_TONES;
}) {
  const style = ALERT_TONES[tone];
  const Icon = style.icon;
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('flex flex-wrap items-center gap-3 rounded-xl border p-4 text-base text-foreground', style.box)}
    >
      <Icon className={cn('h-5 w-5 shrink-0 self-start', style.iconClass)} aria-hidden="true" />
      <span className="min-w-0 flex-1">{children}</span>
      {action}
    </div>
  );
}

/** Announced politely, so a screen reader is told the page is working. */
export function Spinner({ label }: { label: string }) {
  return (
    <p className="flex items-center gap-2.5 py-6 text-base text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden="true" />
      <span role="status">{label}</span>
    </p>
  );
}

/** Visible only to screen readers. */
export function VisuallyHidden({ children }: { children: ReactNode }) {
  return <span className="sr-only">{children}</span>;
}

const TILE_TONES = {
  primary: 'bg-accent text-primary-strong',
  hero: 'bg-hero/10 text-hero',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/20 text-warning-foreground',
  danger: 'bg-danger/10 text-danger',
  muted: 'bg-muted text-muted-foreground',
} as const;

const TILE_SIZES = {
  sm: 'h-9 w-9 rounded-lg [&>svg]:h-4 [&>svg]:w-4',
  md: 'h-11 w-11 rounded-xl [&>svg]:h-5 [&>svg]:w-5',
  lg: 'h-14 w-14 rounded-2xl [&>svg]:h-7 [&>svg]:w-7',
} as const;

/** An icon on a soft square: categories, branches, conditions, states. Always decorative. */
export function IconTile({
  icon: Icon,
  tone = 'primary',
  size = 'md',
  className,
}: {
  icon: LucideIcon;
  tone?: keyof typeof TILE_TONES;
  size?: keyof typeof TILE_SIZES;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn('grid shrink-0 place-items-center', TILE_TONES[tone], TILE_SIZES[size], className)}
    >
      <Icon />
    </span>
  );
}

const CHIP_TONES = {
  muted: 'bg-muted text-muted-foreground',
  primary: 'bg-accent text-primary-strong',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/20 text-warning-foreground',
  danger: 'bg-danger/10 text-danger',
} as const;

/** A short fact in a pill: "15 minutes", "In person", "Walk-ins welcome". */
export function Chip({
  icon: Icon,
  tone = 'muted',
  children,
  className,
}: {
  icon?: LucideIcon;
  tone?: keyof typeof CHIP_TONES;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-sm font-medium',
        CHIP_TONES[tone],
        className,
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
      {children}
    </span>
  );
}

/**
 * A choice that is a whole tile: a condition, a branch, a service a condition leads to. The
 * title and the line under it are the button's name, so a screen reader hears what it chooses.
 */
export function ChoiceCard({
  icon,
  title,
  detail,
  meta,
  className,
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'title'> & {
  icon?: LucideIcon;
  title: ReactNode;
  detail?: ReactNode;
  meta?: ReactNode;
}) {
  return (
    <button
      type="button"
      className={cn(
        'group flex h-full w-full items-start gap-4 rounded-xl border border-border bg-card p-4 text-left shadow-sm',
        'transition-[border-color,background-color,box-shadow] hover:border-primary/60 hover:bg-accent/40 hover:shadow-card',
        FOCUS_RING,
        className,
      )}
      {...rest}
    >
      {icon && <IconTile icon={icon} />}
      <span className="min-w-0 flex-1 self-center">
        <span className="block text-base font-semibold text-heading">{title}</span>
        {detail && <span className="mt-0.5 block text-sm text-muted-foreground">{detail}</span>}
        {meta && <span className="mt-2 flex flex-wrap gap-2">{meta}</span>}
      </span>
      <ChevronRight
        className="mt-3 h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary-strong motion-reduce:transition-none"
        aria-hidden="true"
      />
    </button>
  );
}

/**
 * Back and Continue. On a phone they ride along the bottom of the screen, so Continue is always a
 * thumb away under a long list of times; from `sm` up they sit after the step, as before. The bar
 * reaches the panel's edges through `--panel-pad`, which only the page's panel sets — inside the
 * pharmacy website's frame there is no panel, and the bar stays within the frame.
 */
export function StepActions({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'sticky bottom-0 z-10 mx-[calc(var(--panel-pad,0px)*-1)] flex flex-wrap items-center gap-3 border-t border-border bg-card/95 px-[var(--panel-pad,0px)] pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur',
        'sm:static sm:z-auto sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:pb-0 sm:pt-2 sm:backdrop-blur-none',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** The tick at the top of a done screen. */
export function SuccessMark() {
  return (
    <span
      aria-hidden="true"
      className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-success/10 ring-8 ring-success/5"
    >
      <span className="grid h-11 w-11 place-items-center rounded-full bg-success text-success-foreground shadow-sm">
        <Check className="h-6 w-6" strokeWidth={3} />
      </span>
    </span>
  );
}

/**
 * A little calendar page for an appointment's day. Decorative: the date is always written out
 * beside it, so it is hidden from screen readers rather than read twice.
 */
export function DateTile({ iso, timeZone, locale }: { iso: string; timeZone: string; locale: string }) {
  const date = new Date(iso);
  const month = new Intl.DateTimeFormat(intlLocale(locale), { month: 'short', timeZone })
    .format(date)
    .replace('.', '');
  const day = new Intl.DateTimeFormat(intlLocale(locale), { day: 'numeric', timeZone }).format(date);
  return (
    <span
      aria-hidden="true"
      className="flex h-16 w-16 shrink-0 flex-col overflow-hidden rounded-xl border border-border bg-card text-center shadow-sm"
    >
      <span className="bg-primary py-0.5 text-xs font-semibold uppercase tracking-wide text-primary-foreground">
        {month}
      </span>
      <span className="flex flex-1 items-center justify-center text-2xl font-semibold tabular-nums text-heading">
        {day}
      </span>
    </span>
  );
}
